import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Gauge,
  Loader,
  RefreshCw,
  Sparkles,
  Target,
  Timer,
  Zap,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ghostBtn, panelClass, selectField, tdClass, thClass, trClass } from '../../governed-mcp-gateway/components/GatewayUi';
import { llmGatewayApi } from '../llmGatewayApi';
import { logLlmGateway } from '../llmGatewayLogger';
import { useLlmGateway } from '../llmGatewayStore';
import type {
  IntentGatewayStatistics,
  IntentStatsDailyPoint,
  IntentStatsEvent,
  IntentStatsFunnelStep,
  RegisteredLlm,
} from '../llmGatewayTypes';

type RangeDays = 7 | 30;
type LoadReason = 'page' | 'refresh' | 'live' | 'range';

const LIVE_REFRESH_MS = 15_000;
const EVENTS_PAGE_SIZE = 100;
const INTENT_CHART_LIMIT = 8;

const FUNNEL_LABELS: Record<string, string> = {
  received: 'Received',
  hadIntents: 'Had intents',
  embedded: 'Embedded',
  searched: 'Searched',
  matched: 'Matched',
};

const OUTCOME_LABELS: Record<string, string> = {
  matched: 'Matched',
  default_model: 'Default',
  bound: 'Bound',
  skipped: 'Skipped',
};

const PALETTE = {
  requests: { light: '#4f46e5', dark: '#818cf8' },
  matched: { light: '#059669', dark: '#34d399' },
  tokens: { light: '#d97706', dark: '#fbbf24' },
  defaultModel: { light: '#7c3aed', dark: '#a78bfa' },
  bound: { light: '#64748b', dark: '#94a3b8' },
  skipped: { light: '#d97706', dark: '#fbbf24' },
  grid: { light: '#e5e7eb', dark: '#262626' },
  axis: { light: '#737373', dark: '#a3a3a3' },
};

function rangeIso(days: RangeDays): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function formatNumber(value: number): string {
  return Math.round(value || 0).toLocaleString();
}

function formatScore(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(2);
}

function formatTime(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatStamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fillDaily(from: string, to: string, points: IntentStatsDailyPoint[]): Array<IntentStatsDailyPoint & { label: string }> {
  const byDate = new Map(points.map((point) => [point.date.slice(0, 10), point]));
  const start = new Date(`${from.slice(0, 10)}T00:00:00Z`);
  const end = new Date(`${to.slice(0, 10)}T00:00:00Z`);
  const filled: Array<IntentStatsDailyPoint & { label: string }> = [];
  for (let cursor = new Date(start); cursor.getTime() <= end.getTime(); cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const key = cursor.toISOString().slice(0, 10);
    const point = byDate.get(key) || { date: key, requests: 0, matched: 0, tokens: 0, avgScore: 0 };
    filled.push({
      ...point,
      label: cursor.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
    });
  }
  return filled;
}

function outcomeClass(outcome: string, isLight: boolean): string {
  const map: Record<string, string> = {
    matched: isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300',
    default_model: isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300',
    bound: isLight ? 'bg-slate-100 text-slate-700' : 'bg-neutral-800 text-neutral-300',
    skipped: isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300',
  };
  return map[outcome] || (isLight ? 'bg-slate-100 text-slate-600' : 'bg-neutral-800 text-neutral-300');
}

export function pickedLlmLabel(event: IntentStatsEvent, models: RegisteredLlm[]): string {
  const stored = (event.pickedLlm || '').trim();
  if (stored) return stored;
  const catalog = models.find((model) => model.id === event.llmId);
  if (catalog?.name) return catalog.name;
  return (event.llmId || '').trim() || '—';
}

function ChartTip({
  active,
  payload,
  label,
  isLight,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string }>;
  label?: string;
  isLight: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={`rounded-lg border px-3 py-2 text-xs shadow-sm ${
        isLight ? 'border-neutral-200 bg-white text-neutral-800' : 'border-neutral-700 bg-neutral-900 text-neutral-100'
      }`}
    >
      {label ? <p className="mb-1.5 font-medium">{label}</p> : null}
      <ul className="space-y-1">
        {payload.map((item) => (
          <li key={item.name} className="flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.color }} />
              {item.name}
            </span>
            <span className="tabular-nums">{formatNumber(Number(item.value) || 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function StatisticsTab({
  gatewayId,
  isLight,
  onToast,
}: {
  gatewayId: string;
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { models } = useLlmGateway();
  const [days, setDays] = useState<RangeDays>(7);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [live, setLive] = useState(false);
  const [updatedAt, setUpdatedAt] = useState('');
  const [stats, setStats] = useState<IntentGatewayStatistics | null>(null);
  const [events, setEvents] = useState<IntentStatsEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsOffset, setEventsOffset] = useState(0);
  const inFlightRef = useRef(false);
  const requestIdRef = useRef(0);
  const offsetRef = useRef(0);

  const load = useCallback(
    async (reason: LoadReason, nextOffset = offsetRef.current) => {
      const silent = reason !== 'range';
      if (silent && inFlightRef.current) {
        logLlmGateway('app', 'intent_stats_refresh_skipped', { reason, gatewayId });
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      inFlightRef.current = true;
      offsetRef.current = nextOffset;
      if (silent) setRefreshing(true);
      else setLoading(true);
      const range = rangeIso(days);
      try {
        if (reason === 'page') {
          const page = await llmGatewayApi.getIntentEvents(gatewayId, { ...range, limit: EVENTS_PAGE_SIZE, offset: nextOffset });
          if (requestId !== requestIdRef.current) return;
          setEvents(page.events);
          setEventsTotal(page.total);
          setEventsOffset(page.offset);
          offsetRef.current = page.offset;
          setUpdatedAt(new Date().toISOString());
          return;
        }
        const [nextStats, page] = await Promise.all([
          llmGatewayApi.getIntentStatistics(gatewayId, range),
          llmGatewayApi.getIntentEvents(gatewayId, { ...range, limit: EVENTS_PAGE_SIZE, offset: nextOffset }),
        ]);
        if (requestId !== requestIdRef.current) return;
        setStats(nextStats);
        setEvents(page.events);
        setEventsTotal(page.total);
        setEventsOffset(page.offset);
        offsetRef.current = page.offset;
        setUpdatedAt(new Date().toISOString());
        if (reason === 'live') {
          logLlmGateway('app', 'intent_stats_live_ok', { gatewayId, requests: nextStats?.totals?.requests || 0, offset: page.offset });
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        const message = error instanceof Error ? error.message : 'Could not load statistics.';
        if (reason === 'range') {
          setStats(null);
          setEvents([]);
          setEventsTotal(0);
          onToast('error', message);
        } else if (reason === 'page') {
          logLlmGateway('warning', 'intent_events_page_failed', { gatewayId, message, offset: nextOffset });
          onToast('error', message);
        } else {
          logLlmGateway('warning', reason === 'refresh' ? 'intent_stats_refresh_failed' : 'intent_stats_live_failed', {
            gatewayId,
            message,
          });
        }
      } finally {
        if (requestId === requestIdRef.current) inFlightRef.current = false;
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    [days, gatewayId, onToast]
  );

  useEffect(() => {
    offsetRef.current = 0;
    setEventsOffset(0);
    void load('range', 0);
  }, [days, gatewayId, load]);

  useEffect(() => {
    if (!live) return undefined;
    void load('live', offsetRef.current);
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      void load('live', offsetRef.current);
    }, LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [live, load]);

  const totals = stats?.totals;
  const daily = useMemo(() => (stats ? fillDaily(stats.from, stats.to, stats.daily || []) : []), [stats]);
  const outcomes = useMemo(
    () => [
      { key: 'matched', name: 'Matched', value: totals?.matched || 0, color: isLight ? PALETTE.matched.light : PALETTE.matched.dark },
      { key: 'default_model', name: 'Default', value: totals?.defaultModel || 0, color: isLight ? PALETTE.defaultModel.light : PALETTE.defaultModel.dark },
      { key: 'bound', name: 'Bound', value: totals?.bound || 0, color: isLight ? PALETTE.bound.light : PALETTE.bound.dark },
      { key: 'skipped', name: 'Skipped', value: totals?.skipped || 0, color: isLight ? PALETTE.skipped.light : PALETTE.skipped.dark },
    ],
    [isLight, totals]
  );
  const intentBars = useMemo(
    () =>
      (stats?.intents || []).slice(0, INTENT_CHART_LIMIT).map((intent) => ({
        name: intent.name || intent.intentId || 'Untitled',
        requests: intent.requests,
        matched: intent.matched,
        matchRate: intent.requests ? Math.round((intent.matched / intent.requests) * 100) : 0,
      })),
    [stats]
  );
  const matchRate = totals?.requests ? Math.round(((totals.matched || 0) / totals.requests) * 100) : 0;
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const axis = isLight ? PALETTE.axis.light : PALETTE.axis.dark;
  const grid = isLight ? PALETTE.grid.light : PALETTE.grid.dark;
  const busy = loading || refreshing;
  const liveTrackOff = isLight ? 'bg-neutral-300' : 'bg-neutral-600';
  const page = Math.floor(eventsOffset / EVENTS_PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(eventsTotal / EVENTS_PAGE_SIZE));
  const rangeStart = eventsTotal ? eventsOffset + 1 : 0;
  const rangeEnd = eventsOffset + events.length;

  return (
    <div className="space-y-5">
      <section className={`${panelClass(isLight)} p-5`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className={`text-sm font-semibold ${title}`}>Intent routing</h2>
            <p className={`mt-1 text-xs ${muted}`}>Live volume, match quality, and the model each request actually used.</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {updatedAt ? <span className={`text-[11px] tabular-nums ${muted}`}>Updated {formatStamp(updatedAt)}</span> : null}
            <select
              className={selectField(isLight)}
              value={days}
              onChange={(event) => setDays(Number(event.target.value) === 30 ? 30 : 7)}
              aria-label="Statistics range"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button
              type="button"
              className={`${ghostBtn(isLight)} !h-8 !text-xs`}
              disabled={busy}
              title="Reload now"
              onClick={() => {
                logLlmGateway('app', 'intent_stats_refresh', { gatewayId, days, offset: offsetRef.current });
                void load('refresh', offsetRef.current);
              }}
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              type="button"
              role="switch"
              aria-checked={live}
              aria-label="Live updates"
              title={`Poll every ${LIVE_REFRESH_MS / 1000} seconds while enabled`}
              className={`${ghostBtn(isLight)} !h-8 !text-xs`}
              onClick={() => {
                const next = !live;
                setLive(next);
                logLlmGateway('app', 'intent_stats_live', { live: next, gatewayId, days });
              }}
            >
              <span className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${live ? 'bg-indigo-600' : liveTrackOff}`}>
                <span className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${live ? 'translate-x-3' : 'translate-x-0'}`} />
              </span>
              Live
            </button>
          </div>
        </div>
        {loading ? (
          <div className={`mt-8 flex items-center justify-center gap-2 text-sm ${muted}`}>
            <Loader size={14} className="animate-spin" />
            Loading statistics
          </div>
        ) : stats ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <Kpi label="Requests" value={formatNumber(totals?.requests || 0)} icon={Activity} isLight={isLight} />
            <Kpi label="Matched" value={`${matchRate}%`} hint={`${formatNumber(totals?.matched || 0)} of ${formatNumber(totals?.requests || 0)}`} icon={Sparkles} isLight={isLight} />
            <Kpi label="Tokens" value={formatNumber(totals?.tokens || 0)} icon={Zap} isLight={isLight} />
            <Kpi label="Avg score" value={formatScore(totals?.avgScore)} icon={Target} isLight={isLight} />
            <Kpi label="Avg routing" value={`${formatNumber(totals?.avgRoutingMs || 0)} ms`} icon={Timer} isLight={isLight} />
            <Kpi label="Avg chat" value={`${formatNumber(totals?.avgChatMs || 0)} ms`} icon={Gauge} isLight={isLight} />
          </div>
        ) : (
          <p className={`mt-6 text-sm ${muted}`}>Statistics could not be loaded for this range.</p>
        )}
      </section>

      {!loading && stats ? (
        <>
          <div className="grid gap-5 xl:grid-cols-3">
            <section className={`${panelClass(isLight)} p-5 xl:col-span-2`}>
              <h3 className={`text-sm font-semibold ${title}`}>Daily volume</h3>
              <p className={`mt-1 text-xs ${muted}`}>Requests and matches by day, with token spend on the right axis.</p>
              <DailyChart points={daily} isLight={isLight} days={days} axis={axis} grid={grid} />
            </section>
            <section className={`${panelClass(isLight)} p-5`}>
              <h3 className={`text-sm font-semibold ${title}`}>Decision mix</h3>
              <p className={`mt-1 text-xs ${muted}`}>How routing resolved over this range.</p>
              <OutcomeDonut rows={outcomes} isLight={isLight} />
            </section>
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            <section className={`${panelClass(isLight)} p-5`}>
              <h3 className={`text-sm font-semibold ${title}`}>Routing funnel</h3>
              <p className={`mt-1 text-xs ${muted}`}>Received through matched intent.</p>
              <FunnelChart steps={stats.funnel || []} isLight={isLight} axis={axis} grid={grid} />
            </section>
            <section className={`${panelClass(isLight)} p-5`}>
              <h3 className={`text-sm font-semibold ${title}`}>Intent mix</h3>
              <p className={`mt-1 text-xs ${muted}`}>Top intents by traffic, with match rate.</p>
              <IntentChart rows={intentBars} isLight={isLight} axis={axis} grid={grid} />
            </section>
          </div>

          <section className={`${panelClass(isLight)} overflow-hidden`}>
            <div className="flex flex-wrap items-start justify-between gap-2 px-5 pt-5">
              <div>
                <h3 className={`text-sm font-semibold ${title}`}>Recent routing logs</h3>
                <p className={`mt-1 mb-3 text-xs ${muted}`}>Newest 100 first. LLM Picked is the model that served the request.</p>
              </div>
            </div>
            {events.length ? (
              <>
                <div className="max-h-[28rem] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className={isLight ? 'bg-neutral-50' : 'bg-neutral-950'}>
                        <th className={thClass()}>Time</th>
                        <th className={thClass()}>Preview</th>
                        <th className={thClass()}>Decision</th>
                        <th className={thClass()}>Intent</th>
                        <th className={thClass()}>Score</th>
                        <th className={thClass()}>LLM Picked</th>
                        <th className={thClass()}>Tokens</th>
                        <th className={thClass()}>Latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <RecentRow
                          key={event.eventId}
                          event={event}
                          intents={stats.intents}
                          models={models}
                          isLight={isLight}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between gap-3 px-5 py-3">
                  <p className={`text-xs ${muted}`}>
                    Showing {formatNumber(rangeStart)}–{formatNumber(rangeEnd)} of {formatNumber(eventsTotal)}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className={`${ghostBtn(isLight)} !h-8`}
                      disabled={page <= 1 || busy}
                      onClick={() => {
                        const next = Math.max(0, eventsOffset - EVENTS_PAGE_SIZE);
                        logLlmGateway('app', 'intent_events_page', { gatewayId, offset: next });
                        void load('page', next);
                      }}
                    >
                      Prev
                    </button>
                    <span className={`text-xs tabular-nums ${muted}`}>
                      {page} / {pageCount}
                    </span>
                    <button
                      type="button"
                      className={`${ghostBtn(isLight)} !h-8`}
                      disabled={page >= pageCount || busy}
                      onClick={() => {
                        const next = eventsOffset + EVENTS_PAGE_SIZE;
                        logLlmGateway('app', 'intent_events_page', { gatewayId, offset: next });
                        void load('page', next);
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className={`px-5 pb-5 text-sm ${muted}`}>No routing events in this range.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
  isLight,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: typeof Activity;
  isLight: boolean;
}) {
  return (
    <div className={`rounded-xl border p-3 ${isLight ? 'border-neutral-200 bg-neutral-50/70' : 'border-neutral-800 bg-neutral-950/40'}`}>
      <div className="flex items-center justify-between gap-2">
        <p className={`text-[11px] font-medium ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{label}</p>
        <span className={isLight ? 'text-indigo-600' : 'text-indigo-300'}>
          <Icon size={13} />
        </span>
      </div>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${isLight ? 'text-neutral-900' : 'text-white'}`}>{value}</p>
      {hint ? <p className={`mt-0.5 text-[11px] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</p> : null}
    </div>
  );
}

function DailyChart({
  points,
  isLight,
  days,
  axis,
  grid,
}: {
  points: Array<IntentStatsDailyPoint & { label: string }>;
  isLight: boolean;
  days: RangeDays;
  axis: string;
  grid: string;
}) {
  if (!points.some((point) => point.requests || point.tokens)) {
    return <p className={`mt-8 text-center text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>No daily volume yet.</p>;
  }
  return (
    <div className="mt-4 h-64">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} interval={days === 30 ? 4 : 0} />
          <YAxis yAxisId="left" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip content={<ChartTip isLight={isLight} />} />
          <Legend wrapperStyle={{ fontSize: 11, color: axis }} />
          <Bar yAxisId="left" dataKey="requests" name="Requests" fill={isLight ? PALETTE.requests.light : PALETTE.requests.dark} radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Line yAxisId="left" type="monotone" dataKey="matched" name="Matched" stroke={isLight ? PALETTE.matched.light : PALETTE.matched.dark} strokeWidth={2} dot={false} />
          <Line yAxisId="right" type="monotone" dataKey="tokens" name="Tokens" stroke={isLight ? PALETTE.tokens.light : PALETTE.tokens.dark} strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function OutcomeDonut({
  rows,
  isLight,
}: {
  rows: Array<{ key: string; name: string; value: number; color: string }>;
  isLight: boolean;
}) {
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  if (!total) {
    return <p className={`mt-8 text-center text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>No routing decisions yet.</p>;
  }
  return (
    <div className="mt-2">
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2}>
              {rows.map((row) => (
                <Cell key={row.key} fill={row.color} />
              ))}
            </Pie>
            <Tooltip content={<ChartTip isLight={isLight} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-1 space-y-1.5">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: row.color }} />
              {row.name}
            </span>
            <span className={`tabular-nums ${isLight ? 'text-neutral-600' : 'text-neutral-300'}`}>
              {formatNumber(row.value)} · {Math.round((row.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FunnelChart({
  steps,
  isLight,
  axis,
  grid,
}: {
  steps: IntentStatsFunnelStep[];
  isLight: boolean;
  axis: string;
  grid: string;
}) {
  const data = steps.map((step) => ({ name: FUNNEL_LABELS[step.step] || step.step, count: step.count || 0 }));
  if (!data.some((step) => step.count)) {
    return <p className={`mt-8 text-center text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>No funnel traffic yet.</p>;
  }
  return (
    <div className="mt-4 h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 28, left: 8, bottom: 0 }}>
          <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis type="category" dataKey="name" width={88} tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip content={<ChartTip isLight={isLight} />} />
          <Bar dataKey="count" name="Requests" fill={isLight ? PALETTE.requests.light : PALETTE.requests.dark} radius={[0, 6, 6, 0]} barSize={14} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function IntentChart({
  rows,
  isLight,
  axis,
  grid,
}: {
  rows: Array<{ name: string; requests: number; matched: number; matchRate: number }>;
  isLight: boolean;
  axis: string;
  grid: string;
}) {
  if (!rows.length) {
    return <p className={`mt-8 text-center text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>No intent traffic in this range.</p>;
  }
  return (
    <div className="mt-4 h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 28, left: 8, bottom: 0 }}>
          <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis type="category" dataKey="name" width={112} tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip content={<ChartTip isLight={isLight} />} />
          <Legend wrapperStyle={{ fontSize: 11, color: axis }} />
          <Bar dataKey="requests" name="Requests" fill={isLight ? PALETTE.requests.light : PALETTE.requests.dark} radius={[0, 6, 6, 0]} barSize={10} />
          <Bar dataKey="matched" name="Matched" fill={isLight ? PALETTE.matched.light : PALETTE.matched.dark} radius={[0, 6, 6, 0]} barSize={10} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RecentRow({
  event,
  intents,
  models,
  isLight,
}: {
  event: IntentStatsEvent;
  intents: IntentGatewayStatistics['intents'];
  models: RegisteredLlm[];
  isLight: boolean;
}) {
  const intentName = intents.find((item) => item.intentId === event.intentId)?.name || event.intentId || '—';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const fail = event.chatOk === false;
  return (
    <tr className={`${trClass(isLight)} ${fail ? (isLight ? 'bg-rose-50/70' : 'bg-rose-500/10') : ''}`}>
      <td className={`${tdClass()} whitespace-nowrap ${muted}`}>{formatTime(event.occurredAt)}</td>
      <td className={`${tdClass()} max-w-[14rem] truncate`} title={event.queryPreview}>
        {event.queryPreview || '—'}
      </td>
      <td className={tdClass()}>
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${outcomeClass(event.outcome, isLight)}`}>
          {OUTCOME_LABELS[event.outcome] || event.outcome || '—'}
        </span>
      </td>
      <td className={tdClass()}>{intentName}</td>
      <td className={tdClass()}>{formatScore(event.score)}</td>
      <td className={`${tdClass()} font-mono text-xs`} title={event.llmId || undefined}>
        {pickedLlmLabel(event, models)}
      </td>
      <td className={tdClass()}>{formatNumber(event.totalTokens)}</td>
      <td className={`${tdClass()} whitespace-nowrap`}>
        {formatNumber(event.routingMs + event.chatMs)} ms
      </td>
    </tr>
  );
}
